/**
 * useRadarFrames.ts — بيان رادار MRMS، ومحوره الزمني.
 *
 * ── لماذا هنا لا داخل RadarTileLayer ──────────────────────────────────────────
 *
 * يُستهلَك في مكانين: `NewWeatherMap` يبني منه **محور الشريط الزمني** حين يكون الرادار
 * فعّالاً، و`RadarTileLayer` يختار منه الإطار المعروض. جلبه مرّتين يعني محورين قد
 * يختلفان — وهو بالضبط ما فعله `useSatelliteFrames` قبله لنفس السبب.
 *
 * ── لماذا صار للرادار محور أصلاً ─────────────────────────────────────────────
 *
 * كانت للطبقة حلقةُ تحريك خاصّة بها، مستقلّة عن الشريط عمداً: الشريط محوره ساعات
 * توقّع النموذج (‏0..24 ساعة **قادمة**) بينما إطارات الرادار ماضيةٌ متباعدة بدقيقتين،
 * فربطهما بالفهرس — كما فعل كود RainViewer عبر `currentTimeIndex % frames.length` —
 * يُنتج مطابقةً بلا معنى.
 *
 * لكن ذلك حلَّ التضارب بإلغاء الشريط للرادار: يسحبه المستخدم فلا يتحرّك شيء. والعلاج
 * الصحيح هو ما يفعله التطبيق **نفسه** مع القمر الصناعي والمحيط منذ البداية — لا فهرسٌ
 * مشترك بل **محورٌ مبدَّل**: حين يكون الرادار فعّالاً يصير الشريط محورَ رصدٍ ماضياً،
 * علاماته أوقات الإطارات الحقيقية. عندها يقرأ المستخدم زمناً صحيحاً، ويتولّى التشغيلَ
 * `useAutoplay` الواحد بدل مؤقّت ثانٍ يتسابق معه.
 */

import { useEffect, useMemo, useState } from 'react';
import type { RadarFlowField } from '../webgl/RadarFlowGLLayer';
import {
    fetchRadarManifest, fetchRadarMosaic, fetchRadarNowcast, RADAR_POLL_MS, radarRastersBase,
    type RadarDomain, type RadarManifest, type RadarMosaic, type RadarNowcast,
} from '../../../services/mrmsRadarService';

export interface RadarFrames {
    manifest: RadarManifest | null;
    /**
     * الفسيفساء حين تكون منشورة: **نطاقٌ واحد** إطاراته لحظات الشبكة، حدوده العالم.
     *
     * تُبنى كنطاقٍ اصطناعي عمداً بدل نوعٍ جديد، فيمرّ في نفس المسار الذي اختُبر
     * طوال اليوم (`framesAround` ← الخانات ← المزج) بلا سطرٍ جديد فيه. والفارق
     * الوحيد أنه واحد لا عشرون — وهو كل المطلوب.
     */
    mosaic: RadarDomain | null;
    /** بادئة بلاطات قناع التغطية حين تكون منشورة. */
    coverageTiles: string | null;
    /** أوقات الإطارات بالثواني، من الأقدم إلى الأحدث. فارغ إن لا رادار. */
    times: number[];
    /**
     * كم من `times` **رصدٌ**؛ ما بعده تنبّؤ الساعة القادمة (radar_nowcast.py).
     * «الآن» على الشريط هو آخر رصد (`observedCount − 1`) لا آخر علامة.
     */
    observedCount: number;
    /** حقل الحركة للانزلاق بين الإطارات — مع بلاطات القيم وحدها. */
    flow: RadarFlowField | null;
}

/** وقت الإطار بالثواني، أو null إن لم يحمل وقتاً صالحاً. */
function frameEpoch(time: string | null): number | null {
    if (!time) return null;
    const ms = Date.parse(time);
    return Number.isFinite(ms) ? Math.floor(ms / 1000) : null;
}

/** خطوة محور الرادار — إيقاع OPERA، وكل ثاني-ونصف إطار من MRMS (دقيقتان). */
export const RADAR_AXIS_STEP_S = 300;
/** طول الحلقة. أطول من الأرشيف المحفوظ لا فائدة منه؛ راجع KEEP_FRAMES في المولّدَين. */
export const RADAR_AXIS_SPAN_S = 2 * 3600;

/**
 * محور **شبكة منتظمة كل 5 دقائق** تغطّي النطاقات كلّها — لا محور النطاق الأكثر إطارات.
 *
 * كان المحور أوقات النطاق الأكثر إطارات، أي MRMS (‏12 × دقيقتين). فرادار أوروبا (‏OPERA،
 * كل 5 د وبتأخّر ~7 د) لم يقع منه على الشريط إلا إطاران أو ثلاثة، فبدت عاصفة المتوسّط
 * ساكنة (شكوى 2026-09-14). والاتّحاد الخام يضاعف العلامات بأوقاتٍ لا جديد عند أكثر
 * النطاقات فيها. الشبكة تحلّ الاثنين: كل نطاق يُسقَط عليها بالزمن (‏`frameAtEpoch`)،
 * و5 دقائق هي أدقّ إيقاع يملك عنده الجميع إطاراً جديداً.
 */
export function useRadarFrames(enabled: boolean): RadarFrames {
    const [manifest, setManifest] = useState<RadarManifest | null>(null);
    const [mosaicMeta, setMosaicMeta] = useState<RadarMosaic | null>(null);
    const [nowcast, setNowcast] = useState<RadarNowcast | null>(null);

    // التنبّؤ يُجدَّد كل عشر دقائق؛ يُسأل مع الفسيفساء ويُستبدل حين يتغيّر أساسه.
    useEffect(() => {
        if (!enabled) { setNowcast(null); return; }
        let cancelled = false;
        const load = () => {
            void fetchRadarNowcast().then((n) => {
                if (cancelled) return;
                setNowcast((prev) => (prev && n && prev.base === n.base
                    && prev.times.at(-1)?.values === n.times.at(-1)?.values ? prev : n));
            });
        };
        load();
        const id = window.setInterval(load, RADAR_POLL_MS);
        return () => { cancelled = true; window.clearInterval(id); };
    }, [enabled]);

    useEffect(() => {
        if (!enabled) { setMosaicMeta(null); return; }
        let cancelled = false;
        const load = () => {
            void fetchRadarMosaic().then((m) => {
                if (cancelled) return;
                // نفس قاعدة البيان: لا نستبدل ما لم تتقدّم آخر لحظة فعلاً، وإلّا
                // أُعيد بناء المصادر وومض الرادار كل دورة استطلاع بلا داعٍ.
                setMosaicMeta((prev) => (
                    prev && m && prev.times.at(-1)?.time === m.times.at(-1)?.time
                        // والقيم تكتمل للّحظات القائمة بلا لحظةٍ جديدة — فتُعدّ أيضاً.
                        && prev.times.filter((t) => t.values).length === m.times.filter((t) => t.values).length
                        ? prev : m));
            });
        };
        load();
        const id = window.setInterval(load, RADAR_POLL_MS);
        return () => { cancelled = true; window.clearInterval(id); };
    }, [enabled]);

    useEffect(() => {
        if (!enabled) { setManifest(null); return; }
        let cancelled = false;
        const load = () => {
            void fetchRadarManifest().then((m) => {
                if (cancelled) return;
                // لا نستبدل الحالة ما لم يتقدّم آخر إطار فعلاً: كائن جديد بنفس المحتوى
                // يُعيد بناء المصادر ويومض الرادار كل دقيقتين بلا داعٍ.
                setManifest((prev) => (prev && m && sameLatest(prev, m) ? prev : m));
            });
        };
        load();
        const id = window.setInterval(load, RADAR_POLL_MS);
        return () => { cancelled = true; window.clearInterval(id); };
    }, [enabled]);

    const times = useMemo(() => {
        if (!manifest) return [];
        let first = Infinity;
        let last = -Infinity;
        for (const d of manifest.domains) {
            for (const f of d.frames) {
                const t = frameEpoch(f.time);
                if (t === null) continue;
                if (t < first) first = t;
                if (t > last) last = t;
            }
        }
        if (!Number.isFinite(first)) return [];
        // تنتهي الشبكة عند أحدث رصد تماماً (لا عند مضاعفٍ مقرَّب قبله) كي لا يُخفي
        // المحور آخر إطار — وهو «الآن» الذي فتح المستخدم الرادار ليراه.
        const start = Math.max(first, last - RADAR_AXIS_SPAN_S);
        const times: number[] = [];
        for (let t = last; t >= start; t -= RADAR_AXIS_STEP_S) times.push(t);
        return times.reverse();
    }, [manifest]);

    const mosaic = useMemo<RadarDomain | null>(() => {
        if (!mosaicMeta) return null;
        /*
         * بلاطات القيم حين تكتمل للشريط **كلّه** فقط: خلط النوعين في الحلقة يعني
         * مزج طبقتين مختلفتين في التنعيم عند كل حدّ إطار. وحتى تكتمل (أوّل نصف ساعة
         * بعد النشر) تبقى الألوان. والمعرّف يتغيّر مع النوع، فتُزال خانات النوع
         * الآخر وتُبنى الجديدة بلا أن تلتقي مصادر من نوعين تحت اسمٍ واحد.
         */
        const enc = mosaicMeta.values;
        const useValues = !!enc && mosaicMeta.times.length > 0
            && mosaicMeta.times.every((t) => !!t.values)
            /*
             * **الأصل منذ 2026-09-24**، و`?radarValues=0` يُعيد الألوان للمقارنة.
             *
             * كانت اختيارية لأن عارضها كان `raster-dem` + `color-relief` في MapLibre، وقِيس
             * أبطأ من الألوان ولا يمزج. والعارض الآن طبقة WebGL خاصّة (RadarFlowGLLayer)
             * بتصميم Windy المقيس من شبكته وشيدره: قيمٌ تُلوَّن على المعالج، والحلقة كلّها
             * في الذاكرة، وانزلاقٌ بحقل حركة بين الإطارين.
             */
            && !/[?&]radarValues=0\b/.test(window.location.search);
        return {
            id: useValues ? 'mosaic-v' : 'mosaic',
            values: useValues ? enc : undefined,
            // العالم كلّه: البلاطات نفسها تحمل موضعها، و`bounds` هنا لا تقصّ شيئاً
            // إلا الطلبات خارج الكرة.
            bounds: { west: -180, east: 180, north: 85, south: -85 },
            frames: [
                ...mosaicMeta.times.map((t) => ({
                    time: t.time, file: '', tiles: useValues ? (t.values as string) : t.prefix,
                })),
                /*
                 * ── تنبّؤ الساعة القادمة بعد آخر رصد (سلوك Windy) ──────────────
                 * طلبه العميل في 2026-09-21 بعد أن طلب إزالة امتداد **النموذج** في
                 * 2026-09-19. والفرق جوهري: هذا دفعٌ للرصد نفسه مع حركته (radar_nowcast.py)،
                 * بالألوان والنعومة نفسها، لساعةٍ واحدة — لا حقل نموذجٍ بدقّة عشرات الكيلومترات.
                 * بلاطات ألوان في الوضع الافتراضي وقيم في وضع القيم؛ وما لا يلي آخر رصد يُسقط.
                 */
                ...(nowcast ? nowcast.times
                    .filter((t) => (frameEpoch(t.time) ?? 0) > (frameEpoch(mosaicMeta.times.at(-1)?.time ?? null) ?? Infinity))
                    .filter((t) => (useValues ? !!t.values : !!t.prefix))
                    .map((t) => ({
                        time: t.time, file: '', tiles: (useValues ? t.values : t.prefix) as string, forecast: true,
                    }))
                    : []),
            ],
            validTime: mosaicMeta.times.at(-1)?.time ?? null,
            width: 0, height: 0, coverage: null,
        } as RadarDomain;
    }, [mosaicMeta, nowcast]);

    // محور الفسيفساء هو شبكتها نفسها: كل خطوة لحظةٌ موجودة فعلاً، فلا تتقدّم
    // الحركة إطارين ثم ثلاثة كما كانت تفعل الشبكة المفروضة على مصادر مختلفة الإيقاع.
    const mosaicTimes = useMemo(() => (mosaic
        ? mosaic.frames.map((f) => frameEpoch(f.time)).filter((t): t is number => t !== null)
        : []), [mosaic]);

    const flowFile = mosaic?.values ? nowcast?.flow : undefined;
    const flow = useMemo<RadarFlowField | null>(() => (flowFile
        ? { url: `${radarRastersBase()}${flowFile.file}`, scale: flowFile.scale, seconds: flowFile.seconds }
        : null), [flowFile]);

    return {
        flow,
        manifest, mosaic, coverageTiles: mosaicMeta?.coverage ?? null,
        times: mosaic ? mosaicTimes : times,
        observedCount: mosaic
            ? mosaic.frames.filter((f) => !f.forecast).length
            : times.length,
    };
}

/**
 * أحدث إطار في النطاق **عند اللحظة المطلوبة أو قبلها**.
 *
 * القبْليّة شرط لا تفصيل: اختيار الأقرب مطلقاً كان سيعرض رصداً لم يقع بعد حين يقف
 * المؤشّر بين إطارين — وهو ما يجعل الرادار يبدو سابقاً للزمن. وقبل أوّل إطار نعرض
 * الأوّل لا شيئاً: نطاقٌ بأرشيف أقصر يثبت على أقدم ما عنده بدل أن يومض ويختفي.
 */
export function frameAtEpoch(domain: RadarDomain, epoch: number) {
    const frames = domain.frames;
    let chosen = frames[0];
    for (const f of frames) {
        const t = frameEpoch(f.time);
        if (t === null || t <= epoch) chosen = f; else break;
    }
    return chosen;
}

/**
 * الإطاران المحيطان باللحظة، وكسر المسافة بينهما.
 *
 * ── لماذا إطاران لا واحد (تصحيح 2026-09-16) ─────────────────────────────────
 * الشريط يتحرّك **متّصلاً**: `displayedEpoch = a + (b − a) × frameFraction`، والكسر
 * يُحرّكه `useAutoplay` ستّين مرّة في الثانية. وطبقة القمر الصناعي تستعمل هذا الكسر
 * لتمزج إطارين فتبدو حركتها سائلة، بينما الرادار كان يستدعي `frameAtEpoch` وحدها:
 * تُقرّب اللحظة إلى **الإطار السابق**، فيبقى اللون ساكناً تماماً طوال خمس دقائق من
 * زمن العرض ثم يقفز دفعةً واحدة. وهذا بالضبط ما رآه المستخدم: «البرق يتحرّك والألوان
 * لا تتحرّك» — البرق زمنه متّصل، والرادار كان مُقرَّباً.
 *
 * فالكسر يُحسب من **أوقات الإطارات الحقيقية** لا من علامات المحور: النطاقات غير
 * متزامنة (‏MRMS كل دقيقتين، OPERA كل خمس)، فلكلٍّ مقطعه الخاصّ الذي يمزج عليه.
 */
export function framesAround(domain: RadarDomain, epoch: number) {
    const frames = domain.frames;
    let i = 0;
    for (let k = 0; k < frames.length; k++) {
        const t = frameEpoch(frames[k].time);
        if (t === null || t <= epoch) i = k; else break;
    }
    const cur = frames[i];
    const next = frames[i + 1] ?? null;
    const ta = frameEpoch(cur?.time ?? null);
    const tb = next ? frameEpoch(next.time) : null;
    const span = ta !== null && tb !== null ? tb - ta : 0;
    const blend = span > 0 ? Math.max(0, Math.min(1, (epoch - (ta as number)) / span)) : 0;
    return { cur, next, blend };
}

/** هل يحمل البيانان نفس أحدث إطار في كل نطاق؟ */
function sameLatest(a: RadarManifest, b: RadarManifest): boolean {
    if (a.domains.length !== b.domains.length) return false;
    return a.domains.every((d, i) => d.frames.at(-1)?.file === b.domains[i].frames.at(-1)?.file);
}
